import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Box, type BoxProps } from './Box'
import { Icon } from './Icon'

export interface CarouselMultiProps extends BoxProps {
  disabled?: boolean
}

export const CarouselMulti = React.forwardRef(function CarouselMulti({
  className,
  children,
  disabled = false,
  ...props
}: CarouselMultiProps = {}, ref) {

  const carousel = useRef<HTMLDivElement | null>(null)
  const scroller = useRef<HTMLDivElement | null>(null)

  const getRootRef = (node: HTMLDivElement | null) => {
    try {
      carousel.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    } catch {}
  }

  const [index, setIndex] = useState<number>(0)
  const [scroll, setScroll] = useState<number>(0)
  const [position, setPosition] = useState<number>(0)
  const [mouseDown, setMouseDown] = useState<boolean>(false)
  const [hasOverflow, setHasOverflow] = useState<boolean>(false)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [isScrolling, setIsScrolling] = useState<boolean>(false)
  const [interval, setIntervalInstance] = useState<NodeJS.Timeout>()
  const [preventScroll, setPreventScroll] = useState<boolean>(false)

  // Computeds

  const disabledPrevArrow = useMemo(() => scroll === 0 || disabled, [scroll, disabled])

  const disabledNextArrow = useMemo(() => {
    if (!hasOverflow) return true
    const dimension = scroller.current?.clientWidth ?? 0
    const overflow = scroller.current?.scrollWidth ?? 0
    return (Math.ceil(scroll) >= overflow - dimension) || disabled
  }, [hasOverflow, scroll, scroller, disabled])

  const showPrevArrow = useMemo(() => hasOverflow && !disabledPrevArrow, [hasOverflow, disabledPrevArrow])

  const showNextArrow = useMemo(() => hasOverflow && !disabledNextArrow, [hasOverflow, disabledNextArrow])

  // Methods

  const captureScroll = useCallback(() => {
    setScroll(scroller.current?.scrollLeft ?? 0)
  }, [scroller, setScroll])

  const getItems = useCallback(() => {
    if (!scroller.current) return []
    return (Array.from(scroller.current.children) as HTMLElement[]).filter((el) => !el.dataset.overlay)
  }, [scroller])

  const getElementOffset = useCallback((el: HTMLElement) => {
    const styles = window.getComputedStyle(el)
    return Math.ceil(el.offsetLeft - parseFloat(styles.marginLeft))
  }, [])

  const getElementDimension = useCallback((el: HTMLElement) => {
    const styles = window.getComputedStyle(el)
    return Math.ceil(
      el.clientWidth +
      parseFloat(styles.marginLeft) +
      parseFloat(styles.marginRight)
    )
  }, [])

  const getIndexByPosition = useCallback((scroll: number) => {
    // Items without overlay
    const items = getItems()
    const max = items.length - 1
    if (disabledNextArrow) return max
    else if (scroll === 0) return 0
    // Get target index
    let i = 0
    for (let item, itemOffset, itemArea; i < items.length; i++) {
      item = items[i]
      itemOffset = getElementOffset(item)
      itemArea = getElementDimension(item) / 2
      if (
        scroll > itemOffset - itemArea &&
        scroll <= itemOffset + itemArea
      ) break
    }
    return Math.min(i, max)
  }, [disabledNextArrow, getItems, getElementOffset, getElementDimension])

  const normalizeIndex = useCallback((val: number) => {
    const items = getItems()
    const max = items.length - 1
    return Math.max(Math.min(val, max), 0)
  }, [getItems])

  const getOverflowState = useCallback(() => {
    if (!scroller.current) return false
    return scroller.current.scrollWidth > scroller.current.clientWidth + 2
  }, [scroller])

  // Navigation

  const navigateTo = useCallback((index: number, smooth = true) => {
    // Normalize index
    const items = getItems()
    if (!items.length) return
    index = normalizeIndex(index)
    // Get scrollTo
    let scrollTo = getElementOffset(items[index])
    if (props.align === 'end') {
      scrollTo -= carousel.current?.clientWidth ?? 0
      scrollTo += getElementDimension(items[index])
    } else if (props.align === 'center') {
      scrollTo -= (carousel.current?.clientWidth ?? 0) / 2
      scrollTo += getElementDimension(items[index]) / 2
    }
    if (index === 0) scrollTo = 0
    // Scroll to target
    scroller.current?.scroll({
      left: scrollTo,
      behavior: smooth ? 'smooth' : 'instant'
    })
    clearInterval(interval)
    if (smooth && scrollTo) {
      setPreventScroll(true)
      setIsScrolling(true)
      setIntervalInstance(setTimeout(() => {
        setIsScrolling(false)
        setPreventScroll(false)
      }, 300))
    }
  }, [getItems, normalizeIndex, getElementOffset, getElementDimension])

  const next = useCallback(() => {
    setIndex((index) => normalizeIndex(index + 1))
  }, [normalizeIndex])

  const prev = useCallback(() => {
    setIndex((index) => normalizeIndex(index - 1))
  }, [normalizeIndex])

  const refresh = useCallback(() => {
    setHasOverflow(getOverflowState())
    navigateTo(index, false)
  }, [getOverflowState, navigateTo, index])

  // Listeners

  const onMouseUp = useCallback(() => {
    captureScroll()
    const newIndex = getIndexByPosition(scroller.current?.scrollLeft ?? 0)
    setMouseDown(false)
    setIndex(newIndex)
    setTimeout(() => {
      setIsDragging(false)
      setPreventScroll(false)
    }, 100)
  }, [captureScroll, getIndexByPosition, navigateTo])

  const onMouseMove = useCallback((e: MouseEvent) => {
    const _move = e.clientX - position
    setIsDragging(isDragging || Math.abs(_move) > 10)
    if (scroller.current) {
      scroller.current.scrollLeft = scroll - _move
    }
  }, [position, scroll, isDragging, scroller])

  const onTouchMove = useCallback((e: TouchEvent) => {
    // @ts-expect-error Type 'TouchEvent' is not assignable to type 'MouseEvent', but is compatible.
    return onMouseMove(e.touches?.[0] ?? e)
  }, [onMouseMove])

  const toggleEvents = useCallback((add = true) => {
    if (add) {
      window.addEventListener('mouseup', onMouseUp)
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('touchend', onMouseUp)
      window.addEventListener('touchmove', onTouchMove)
    } else {
      window.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('touchend', onMouseUp)
      window.removeEventListener('touchmove', onTouchMove)
    }
  }, [onMouseUp, onMouseMove, onTouchMove])

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setMouseDown(!preventScroll)
    setPreventScroll(!preventScroll)
    setHasOverflow(getOverflowState())
    setPosition(e.clientX)
  }, [preventScroll, getOverflowState])

  const onTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    // @ts-expect-error Type 'TouchEvent' is not assignable to type 'MouseEvent', but is compatible.
    onMouseDown(e.touches?.[0] ?? e)
  }, [onMouseDown])

  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    // !preventScroll && e.preventDefault()
    if (!isScrolling && !preventScroll) {
      const delta = e.deltaX || e.deltaY
      if (delta > 0) next()
        else prev()
    }
  }, [preventScroll, isScrolling, next, prev])

  const onScroll = useCallback(() => {
    if (!mouseDown) captureScroll()
  }, [mouseDown, captureScroll])

  // Watchers

  useEffect(() => {
    window.addEventListener('resize', refresh)
    return () => window.removeEventListener('resize', refresh)
  }, [refresh])

  useEffect(() => {
    toggleEvents(mouseDown)
    return () => toggleEvents(false)
  }, [toggleEvents, mouseDown])

  useEffect(() => {
    navigateTo(index)
  }, [index, navigateTo])

  useEffect(() => {
    window.requestAnimationFrame(refresh)
  }, [disabled])

  return (
    <Box
      ref={getRootRef}
      className={['topbar-carousel-multi', isDragging && 'is-dragging', { disabled },  className]}
      {...props}
    >
      <div
        ref={scroller}
        className="topbar-carousel-multi__scroller"
        onTouchStart={onTouchStart}
        onMouseDown={onMouseDown}
        onScroll={onScroll}
        onWheel={onWheel}
      >
        {children}
        {isDragging && <div className="topbar-carousel-multi__overlay" />}
      </div>
      {showPrevArrow && (
        <div className="topbar-carousel-multi__arrow-prev">
          <Icon
            name="icon-arrow-ios-back-outline"
            size="12"
            onClick={prev}
          />
        </div>
      )}
      {showNextArrow && (
        <div className="topbar-carousel-multi__arrow-next">
          <Icon
            name="icon-arrow-ios-forward-outline"
            size="12"
            onClick={next}
          />
        </div>
      )}
    </Box>
  )
})